<template lang="pug">
  .search-background.width-100(v-if='schoolGroupVO')
    form.main-container.search-container.row.middle(@submit.prevent='search' novalidate)
      .form-group.search-input
        label.text-small.white {{ $t('dashboard.topic') }}
        input.input-base(type='text' name='name' v-model='filterValues.name' :placeholder='$t("dashboard.searchPlaceholder")')
      .form-group(v-if='isKrea')
        label.text-small.white {{ $t('dashboard.creations') }}
        k-select.m4-top-2(
          name='author'
          :options='creationsOptions'
          @selectOption='updateFilterValue'
          :emptyOption='$t("dashboard.creationsEmptyLabel")'
          :preselectedOption='filterValues.author'
        )
      .form-group(v-if='agesOptions.length > 1 && !isKrea')
        label.text-small.white {{ $t('dashboard.age') }}
        k-select.m4-top-2(
          name='ageRangeIds'
          :options='agesOptions'
          valueName='id'
          @selectOption='updateFilterValue'
          :emptyOption='$t("dashboard.ageEmptyLabel")'
          :preselectedOption='filterValues.ageRangeId'
        )
      .form-group(v-if='!isKrea')
        label.text-small.white {{ $t('dashboard.language') }}
        k-select.m4-top-2(
          name='language'
          :options='supportedLanguages'
          keyName='lang'
          valueName='lang'
          @selectOption='updateFilterValue'
          :emptyOption='$t("dashboard.languageEmptyLabel")'
          :preselectedOption='filterValues.language'
        )
      .row.buttons-container.self-end.middle.width-100
        button.primary-button.white-border.search-button.m4-right-3.with-icon(type='submit')
          simple-svg.search-icon.white-icon(:filepath='searchIcon' :width='"15px"' :height='"15px"')
        .row.end.width-100.displays
            button.navigation-button.with-icon.row.middle(type='button' :class='{active: navigationOptions.list}' @click='handleNavigationOptions("list")')
              | {{ $t("dashboard.list") }}
              simple-svg.m-left-1(
                :filepath='navigationListIcon'
                width="17px"
                height="17px"
                :class='navigationOptions.list ? "dark-gray-stroke-icon" : "white-icon"'
              )
            button.navigation-button.with-icon.row.middle(type='button' :class='{active: navigationOptions.card}' @click='handleNavigationOptions("card")')
              | {{ $t("dashboard.grid") }}
              simple-svg.m-left-05(
                :filepath='navigationCardIcon'
                width="20px"
                height="20px"
                :class='navigationOptions.card ? "dark-gray-stroke-icon" : "white-icon"'
              )
</template>

<script>
import { mapState, mapActions } from 'vuex'
import 'vue-slider-component/theme/default.css'
import amplitude from 'amplitude-js'

import KSelect from '@/components/KSelect'
import navigationListIcon from '@/assets/ic_display_list.svg'
import navigationCardIcon from '@/assets/ic_display_grid.svg'
import searchIcon from '@/assets/ic_search.svg'

import { supportedLanguages } from '../../constants/languages'
import filtersMixin from '../../mixins/filters'
import { defaultPageSize, defaultProgramsPageSize } from '../../utils/constants'
import { kreaCreationsFilter } from '@/constants/krea'
import { generateEducationalProgramFilters } from '../../utils/filters'
import userRole from '../../mixins/userRole'
import { routes } from '../../router'
import { i18n } from '../../main'

export default {
  components: { KSelect },
  mixins: [ filtersMixin, userRole ],
  props: {
    isKrea: { type: Boolean },
    drafts: { type: Boolean }
  },
  data() {
    return {
      supportedLanguages,
      navigationListIcon,
      navigationCardIcon,
      searchIcon,
      defaultPageSize
    }
  },
  computed: {
    ...mapState({
      ages: state => state.dashboard.ages,
      categories: state => state.dashboard.categories,
      programsOptions: state => state.educationalProgram.navigationOptions,
      kreaOptions: state => state.kreaEducationalContents.navigationOptions
    }),
    navigationOptions() {
      return this.isKrea ? this.kreaOptions : this.programsOptions
    },
    creationsOptions() {
      return Object
        .keys(kreaCreationsFilter)
        .map(key => ({
          id: kreaCreationsFilter[key],
          label: i18n.t(`krea.creationsTypes.${key}`)
        }))
    },
    agesOptions() {
      return this.ages
        .filter(age => this.userEducationalLevels.includes(age.educationalLevel))
        .map(age => ({
          ...age,
          label: `\
          <div class='age-option' style='--color: ${age.mainColor}'>\
            ${this.$t('resourceCard.agesTag', { min: age.minAge, max: age.maxAge })}\
          </div>`
        }))
    }
  },
  methods: {
    ...mapActions([
      'setNavigationOptions',
      'setKreaNavigationOptions'
    ]),
    search() {
      const filters = generateEducationalProgramFilters(this.filterValues)
      amplitude.getInstance().logEvent('Educational programs search', filters)
      this.$emit('searchedPrograms', filters)
      if (this.isKrea) {
        if (this.drafts) this.$router.push({ name: routes.kreaDrafts, query: { ...filters }, params: { hasReloaded: false } })
        else this.$router.push({ name: routes.kreaPublished, query: { ...filters }, params: { hasReloaded: false } })
      } else this.$router.push({ name: routes.educationalPrograms, query: { ...filters }, params: { hasReloaded: false } })
    },
    handleNavigationOptions(option) {
      if (this.isKrea) this.setKreaNavigationOptions({ option })
      else this.setNavigationOptions({ option })
      if (option === 'card') this.$emit('changePageSize', 8)
      else this.$emit('changePageSize', defaultProgramsPageSize)
    }
  }
}
</script>

<style lang="scss" scoped>
@import 'src/scss/variables/colors';
@import 'src/scss/variables/sizes';

.search-icon {
  height: 15px;
  width: 15px;
}

.displays{
  &>:nth-child(1){
    border-radius: 5px 0 0 5px;
    border-right: none;
  }
  &>:nth-child(2){
    border-radius: 0 5px 5px 0;
    border-left: none;
  }
}

.search-background {
  background-color: $blue;
  height: $searchbar-height;
  padding: 20px 0 32px;
}

.rotate-icon-90 {
  transform: rotate(90deg);
}

.navigation-button {
  align-items: center;
  display: flex;
  justify-content: center;
  padding: 0 10px;
  line-height: 19px;
  height: 40px;
  border: 2px solid $light-gray;
  color: $white;
  &.active{
    color: $dark-gray;
    font-weight: bold;
    background-color: $light-gray;
  }
}

.search-container {
  height: 100%;
  position: relative;

  & > .form-group {
    --arrow-height: 16px;
    --arrow-width: 16px;
    --font-size: 14px;
    --input-height: 36px;
    --line-height: 17px;
    --select-container-width: 228px;
    margin-right: 14px;
    max-width: 170px;
  }

  .search-input {
    position: relative;

    .input-base {
      height: 36px;
      padding-right: 30px;

      &::placeholder {
        font-size: 14px;
      }
    }
  }

  .buttons-container {
    .search-button {
      --max-width: 50px;
      width: 100%;
      height: 36px;
      margin-top: 3px;
    }

    .advanced-filters {
      width: 100%;
    }

    .filter-icon {
      height: 20px;
      width: 20px;
    }
  }

  .advanced-filters-container {
    background-color: $white;
    border-radius: 4px;
    box-shadow: 0 2px 8px 0 $shadow-gray;
    padding: 40px;
    position: absolute;
    right: 0;
    top: calc(100% + 20px);
    transition: all .3s ease;
    z-index: 2;

    &:before {
      background: url('../../assets/ic_triangle.svg') no-repeat;
      content: '';
      height: 15px;
      position: absolute;
      right: 4px;
      top: -8px;
      width: 30px;
    }

    .classes-range-slider {
      margin: 0 8px;
    }

    .classes-range-input {
      border: 1px solid $light-gray;
      border-radius: 6px;
      color: $dark-gray;
      height: 24px;
      text-align: center;
      width: 48px;
    }
  }
}
</style>
